import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Party, PartyInput, PartyKind, Site, SiteGeo, SiteInput, UUID } from '@/api/types';
import type { Decimal } from '@/lib/format/decimal';

export const partyKeys = {
  all: ['parties'] as const,
  list: (kind: PartyKind, q: string) => ['parties', 'list', kind, q] as const,
  detail: (id: UUID) => ['parties', 'detail', id] as const,
};

export const siteKeys = {
  all: ['sites'] as const,
  byCustomer: (customerId: UUID) => ['sites', 'customer', customerId] as const,
  detail: (id: UUID) => ['sites', 'detail', id] as const,
};

export const partiesQuery = (kind: PartyKind, q = '', size = 50) =>
  queryOptions({
    queryKey: partyKeys.list(kind, q),
    queryFn: ({ signal }) => api<Party[]>('/parties', { query: { kind, q, size }, signal }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

const PAGE = 50;

/** Paged list with "Cargar más" (the API returns plain pages without totals). */
export const partiesInfiniteQuery = (kind: PartyKind, q = '') =>
  infiniteQueryOptions({
    queryKey: [...partyKeys.list(kind, q), 'infinite'],
    queryFn: ({ pageParam, signal }) => api<Party[]>('/parties', { query: { kind, q, page: pageParam, size: PAGE }, signal }),
    initialPageParam: 0,
    getNextPageParam: (last, _all, page) => (last.length === PAGE ? page + 1 : undefined),
    placeholderData: (prev) => prev,
  });

export const partyQuery = (id: UUID | null | undefined) =>
  queryOptions({
    queryKey: partyKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api<Party>(`/parties/${id}`, { signal }),
    enabled: !!id,
    staleTime: 60_000,
  });

export const sitesQuery = (customerId: UUID | null | undefined) =>
  queryOptions({
    queryKey: siteKeys.byCustomer(customerId ?? ''),
    queryFn: ({ signal }) => api<Site[]>('/sites', { query: { customerId }, signal }),
    enabled: !!customerId,
    staleTime: 30_000,
    // Google Maps fills the distance a few seconds after saving: ask again every 2 s for up to ~30 s.
    refetchInterval: (q) =>
      q.state.data?.some((s) => !s.distanceKm && !s.distanceError) && q.state.dataUpdateCount < 15 ? 2000 : false,
  });

export function useCreateParty() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PartyInput) => api<Party>('/parties', { body: input }),
    onSuccess: (party) => {
      client.setQueryData(partyKeys.detail(party.id), party);
      void client.invalidateQueries({ queryKey: partyKeys.all });
    },
  });
}

export function useUpdateParty() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, party, active }: { id: UUID; version: number; party: PartyInput; active: boolean }) =>
      api<Party>(`/parties/${id}`, { method: 'PUT', body: { version, party, active } }),
    onSuccess: (party) => {
      client.setQueryData(partyKeys.detail(party.id), party);
      void client.invalidateQueries({ queryKey: partyKeys.all });
    },
  });
}

export function useCreateSite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SiteInput) => api<Site>('/sites', { body: input }),
    onSuccess: (site) => {
      client.setQueryData(siteKeys.detail(site.id), site);
      void client.invalidateQueries({ queryKey: siteKeys.byCustomer(site.customerId) });
    },
  });
}

export function useUpdateSite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: UUID;
      version: number;
      label: string;
      address: string;
      locality: string | null;
      notes: string | null;
      geo?: SiteGeo | null;
    }) => api<Site>(`/sites/${id}`, { method: 'PUT', body }),
    onSuccess: (site) => {
      client.setQueryData(siteKeys.detail(site.id), site);
      void client.invalidateQueries({ queryKey: siteKeys.byCustomer(site.customerId) });
    },
  });
}

export function useRecalculateDistance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, replaceManual }: { id: UUID; replaceManual: boolean }) =>
      api<Site>(`/sites/${id}/distance/recalculate`, { method: 'POST', query: { replaceManual } }),
    onSuccess: (site) => void client.invalidateQueries({ queryKey: siteKeys.byCustomer(site.customerId) }),
  });
}

export function useManualDistance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, km, reason }: { id: UUID; km: Decimal; reason: string }) =>
      api<Site>(`/sites/${id}/distance`, { method: 'PUT', body: { km, reason } }),
    onSuccess: (site) => void client.invalidateQueries({ queryKey: siteKeys.byCustomer(site.customerId) }),
  });
}
